import { useEffect, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Autocomplete, Box, Chip, TextField, Typography,
    Stack
} from '@mui/material';
import * as api from './departmentsApi';
import type { Department, MainGroup } from './departmentsApi';
import { useDetailView } from '../common/useDetailView';
import { sx13, FormRow, DetailViewShell } from '../common/detailView';

interface Props {
    departmentId: number | null;
}

export function DepartmentView({ departmentId }: Props) {
    const { t } = useTranslation();
    const detail = useDetailView<Department>();

    const [mainGroups, setMainGroups] = useState<MainGroup[]>([]);

    const load = useCallback(async () => {
        await detail.withLoading(async () => {
            const mgs = await api.getMainGroups();
            setMainGroups(mgs);
            if (departmentId === null) {
                detail.setItem({ id: 0, departmentCode: '', departmentDescription: '', mainGroups: [] });
                detail.setIsNew(true);
                detail.setIsEditing(true);
            } else {
                const data = await api.getDepartment(departmentId);
                data.mainGroups = data.mainGroups ?? [];
                detail.setItem(data);
                detail.setIsNew(false);
                detail.setIsEditing(false);
            }
        });
    }, [departmentId]);

    useEffect(() => { load(); }, [load]);

    const item = detail.item;

    const save = async () => {
        if (!item) return;
        await detail.withSaving(async () => {
            const response = await api.saveDepartment(item);
            response.mainGroups = response.mainGroups ?? [];
            detail.setItem(response);
            detail.setIsNew(false);
            detail.setIsEditing(false);
        });
    };

    return (
        <DetailViewShell<Department>
            title={`${item?.departmentCode ?? ''} ${item?.departmentDescription ?? ''}`}
            isEditing={detail.isEditing}
            isNew={detail.isNew}
            saving={detail.saving}
            canSave={!!item && !!item.departmentCode && !!item.departmentDescription}
            loading={detail.loading}
            error={detail.error}
            item={item}
            onEdit={detail.startEdit}
            onSave={save}
            onCancel={detail.cancelEdit}
            onClearError={() => detail.setError(null)}
        >
            {(department) => {
                const isCodeValid = !!department.departmentCode;
                const isDescriptionValid = !!department.departmentDescription;

                return (<>
                    <FormRow label={t('department.departmentCode')}>
                        {!detail.isEditing ? (
                            <Typography sx={sx13}>{department.departmentCode}</Typography>
                        ) : (
                            <TextField size="small" fullWidth
                                value={department.departmentCode}
                                onChange={e => detail.update('departmentCode', e.target.value)}
                                error={!isCodeValid}
                                helperText={!isCodeValid ? t('common.required') : undefined} />
                        )}
                    </FormRow>

                    <FormRow label={t('department.departmentDescription')}>
                        {!detail.isEditing ? (
                            <Typography sx={sx13}>{department.departmentDescription}</Typography>
                        ) : (
                            <TextField size="small" fullWidth
                                value={department.departmentDescription}
                                onChange={e => detail.update('departmentDescription', e.target.value)}
                                error={!isDescriptionValid}
                                helperText={!isDescriptionValid ? t('common.required') : undefined} />
                        )}
                    </FormRow>

                    <FormRow label={t('department.mainGroups')}>
                        {!detail.isEditing ? (
                            <Box>
                                {department.mainGroups.map(mg => (
                                    <Typography sx={sx13} key={mg.id}>
                                        {mg.mainGroupCode} {mg.mainGroupDescription}
                                    </Typography>
                                ))}
                            </Box>
                        ) : (
                            <Autocomplete
                                multiple
                                size="small"
                                options={[...mainGroups].sort((a, b) => a.mainGroupCode.localeCompare(b.mainGroupCode))}
                                getOptionLabel={o => `${o.mainGroupCode} ${o.mainGroupDescription}`}
                                value={department.mainGroups}
                                onChange={(_, value) => detail.update('mainGroups', value)}
                                isOptionEqualToValue={(o, v) => o.id === v.id}
                                renderInput={params => <TextField {...params} />}
                                renderTags={(value, getTagProps) =>
                                    value.map((mg, i) => (
                                        <Chip {...getTagProps({ index: i })} key={mg.id}
                                            label={`${mg.mainGroupCode} ${mg.mainGroupDescription}`} size="small" />
                                    ))
                                }
                            />
                        )}
                    </FormRow>
                </>);
            }}
        </DetailViewShell>
    );
}
